import { Box, CardContent, Grid, Stack, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { DateTime } from 'luxon';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { QueryJobSiteCounty, QueryJobSiteHistoryMonth } from '../../api/schema';
import { CardHeaderFlex } from '../../component/cardHeaderFlex/CardHeaderFlex';
import KPI from '../../component/kpi/KPI';
import LinearProgress from '../../component/linearProgress/LinearProgress';
import { CountyMap } from '../../component/map/CountyMap';
import Table from '../../component/table/Table';
import { getLastRefresh } from '../../redux/slices/app/appSlice';
import {
  getDashboardJobSiteCounties,
  getDashboardJobSiteHistory,
  getDashboardJobSiteKpis,
  getDashboardJobSiteStarts,
  getJobSiteCounties,
  getJobSiteCountiesFetching,
  getJobSiteHistory,
  getJobSiteHistoryFetching,
  getJobSiteKpis,
  getJobSiteKpisFetching,
  getJobSiteStarts,
  getJobSiteStartsFetching,
} from '../../redux/slices/dashboard/dashboardSlice';
import { useDispatch } from '../../redux/store';
import { Card, CardStyled } from '../../styled/components';
import { ActivityHorizonControls } from '../dashboard/ActivityHorizonControls';
import { JobStartsChart } from '../dashboard/JobStartsChart';
import { viewerTimeZone } from '../dashboard/kpiWindow';
import { ACTIVITY_LEAD_PERIODS, ACTIVITY_PERIODS, ActivityGranularity } from '../dashboard/periodChart';

// The Job Sites dashboard (Customers > Dashboard, Remediation organizations):
// KPI tiles on top; then average daily jobs started by day, week or month
// beside open job sites by county, shown as a map or a table; history
// underneath (one row per month of job starts). The backend also reports items
// on site for over 30 days per month; that column is held back until its
// numbers are trusted.

const HISTORY_MONTHS = 12;

type MapScope = 'region' | 'all';
type CountyView = 'map' | 'table';

const days = (value?: number | null): string => (value === null || value === undefined ? '' : Math.round(value).toLocaleString());

export const JobSiteDashboard = (): ReactElement => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isLgUp = useMediaQuery(theme.breakpoints.up('lg'), { noSsr: true });
  const kpis = useSelector(getDashboardJobSiteKpis);
  const counties = useSelector(getDashboardJobSiteCounties);
  const history = useSelector(getDashboardJobSiteHistory);
  const kpisFetching = useSelector(getJobSiteKpisFetching);
  const countiesFetching = useSelector(getJobSiteCountiesFetching);
  const historyFetching = useSelector(getJobSiteHistoryFetching);
  // The global refresh button bumps lastRefresh; including it re-runs the fetch.
  const lastRefresh = useSelector(getLastRefresh);
  const tz = useMemo(() => viewerTimeZone(), []);
  // The map opens on the NYC tri-state counties whether or not they hold a
  // site; "all" widens it to every county that does and fits the map to them.
  const [scope, setScope] = useState<MapScope>('region');
  const [countyView, setCountyView] = useState<CountyView>('map');
  const [granularity, setGranularity] = useState<ActivityGranularity>('day');
  const [showTrend, setShowTrend] = useState(true);
  const starts = useSelector(getDashboardJobSiteStarts);
  const startsFetching = useSelector(getJobSiteStartsFetching);
  // Both cards of the row are as tall as the map; the map fits its counties
  // to whatever height it gets.
  const countyHeight = isLgUp ? 544 : 336;

  useEffect(() => {
    dispatch(getJobSiteKpis({ tz }));
    dispatch(getJobSiteCounties());
    dispatch(getJobSiteHistory({ tz, months: HISTORY_MONTHS }));
  }, [dispatch, tz, lastRefresh]);

  useEffect(() => {
    dispatch(getJobSiteStarts({ tz, granularity, periods: ACTIVITY_PERIODS[granularity] + ACTIVITY_LEAD_PERIODS }));
  }, [dispatch, tz, granularity, lastRefresh]);

  const mapCounties = useMemo(
    () => (scope === 'region' ? counties.filter((county) => county.in_default_region) : counties.filter((county) => (county.open_sites ?? 0) > 0)),
    [counties, scope],
  );
  // The table only ever lists counties with something on them; the region
  // toggle narrows it to the same counties the map is showing.
  const tableCounties = useMemo(() => mapCounties.filter((county) => (county.open_sites ?? 0) > 0), [mapCounties]);
  const outsideRegion = useMemo(() => counties.some((county) => !county.in_default_region && (county.open_sites ?? 0) > 0), [counties]);

  const countyColumns = useMemo(
    () => [
      { Header: 'State', accessor: 'state', width: '12%' },
      { Header: 'County', accessor: 'county' },
      { Header: 'Job Sites', accessor: 'open_sites', width: '15%' },
      { Header: 'Equipment', accessor: 'equipment_count', width: '15%' },
      {
        Header: 'Avg Days Open',
        accessor: 'avg_days_open',
        width: '18%',
        Cell: ({ row }: { row: { original: QueryJobSiteCounty } }) => days(row.original.avg_days_open),
      },
    ],
    [],
  );

  const historyColumns = useMemo(
    () => [
      {
        Header: 'Month',
        accessor: 'month',
        Cell: ({ row }: { row: { original: QueryJobSiteHistoryMonth } }) =>
          row.original.month ? DateTime.fromISO(row.original.month).toFormat('LLL yyyy') : '',
      },
      { Header: 'Jobs Opened', accessor: 'sites_opened' },
      {
        Header: 'Avg Job Days',
        accessor: 'avg_duration_days',
        Cell: ({ row }: { row: { original: QueryJobSiteHistoryMonth } }) => days(row.original.avg_duration_days),
      },
      { Header: 'Equipment Delivered', accessor: 'equipment_delivered' },
      {
        Header: 'Avg Days On Site',
        accessor: 'avg_equipment_onsite_days',
        Cell: ({ row }: { row: { original: QueryJobSiteHistoryMonth } }) => days(row.original.avg_equipment_onsite_days),
      },
    ],
    [],
  );

  const tiles = [
    { title: 'Open Job Sites', amount: kpis?.open_sites, color: kpis?.open_sites ? 'blue' : 'black' },
    { title: 'Equipment On Site', amount: kpis?.equipment_onsite, color: kpis?.equipment_onsite ? 'blue' : 'black' },
    { title: 'Avg Days Open', amount: kpis?.avg_days_open, color: 'black' },
    { title: 'Opened This Month', amount: kpis?.opened_this_month, color: 'black' },
    { title: 'Cleared This Month', amount: kpis?.cleared_this_month, color: 'black' },
  ];

  return (
    <>
      {(kpisFetching || countiesFetching || historyFetching || startsFetching) && <LinearProgress />}
      <Grid container spacing={{ xs: 2, md: 4 }} justifyContent="center" sx={{ mb: 4 }}>
        {tiles.map((tile) => (
          <Grid item xs={6} sm={4} md={2.4} key={tile.title}>
            <KPI title={tile.title} data={[{ amount: tile.amount, color: tile.color }]} fetching={kpisFetching} />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={{ xs: 2, md: 4 }} sx={{ mb: 4 }}>
        <Grid item xs={12} lg={5}>
          <CardStyled sx={{ height: '100%' }}>
            <CardHeaderFlex
              title="Average Daily Jobs Started"
              legend={
                <ActivityHorizonControls
                  caption="Jobs per day"
                  granularity={granularity}
                  onGranularity={setGranularity}
                  showTrend={showTrend}
                  onShowTrend={setShowTrend}
                />
              }
            />
            <CardContent sx={{ pt: 0, '&:last-child': { pb: 2 } }}>
              <JobStartsChart
                rows={starts}
                granularity={granularity}
                fetching={startsFetching}
                showTrend={showTrend}
                height={isLgUp ? countyHeight - 30 : 260}
              />
            </CardContent>
          </CardStyled>
        </Grid>
        <Grid item xs={12} lg={7}>
          <CardStyled sx={{ height: '100%' }}>
            <CardHeaderFlex
              title="Open Job Sites by County"
              legend={
                <Stack direction="row" alignItems="center" spacing={1}>
                  {/* the scope narrows the map and the table alike */}
                  <ToggleButtonGroup size="small" exclusive value={scope} onChange={(_, value: MapScope | null) => value && setScope(value)}>
                    <ToggleButton value="region">Tri-State</ToggleButton>
                    <ToggleButton value="all" disabled={!outsideRegion}>
                      All
                    </ToggleButton>
                  </ToggleButtonGroup>
                  <ToggleButtonGroup size="small" exclusive value={countyView} onChange={(_, value: CountyView | null) => value && setCountyView(value)}>
                    <ToggleButton value="map">Map</ToggleButton>
                    <ToggleButton value="table">Table</ToggleButton>
                  </ToggleButtonGroup>
                </Stack>
              }
            />
            <CardContent sx={{ p: 1, pt: 0, '&:last-child': { pb: 1 } }}>
              {countyView === 'map' ? (
                <CountyMap counties={mapCounties} height={countyHeight} />
              ) : (
                // Same height as the map, scrolling inside, so switching views
                // does not move the page.
                <Box sx={{ height: countyHeight, overflowY: 'auto' }}>
                  <Table
                    columns={countyColumns}
                    data={tableCounties}
                    loading={countiesFetching}
                    size="small"
                    withPagination={false}
                    noGlobalFilter
                    noDataText="No open job sites"
                  />
                </Box>
              )}
            </CardContent>
          </CardStyled>
        </Grid>
      </Grid>

      <Card>
        <Table
          title="History"
          columns={historyColumns}
          data={history}
          loading={historyFetching}
          size="small"
          withPagination={false}
          noGlobalFilter
          noDataText="No job sites yet"
        />
      </Card>
    </>
  );
};
